// =============================================================================
// MobileDashboard.tsx
// Mobile Dashboard tab — 4 stat cards (mirror desktop StatCards), search +
// filters + add row, then the order list. Stat cards are tap-to-filter.
// =============================================================================

import React from 'react';

import { EmptyState } from '../ui';

import { formatCurrency } from '../../lib/utils';

import { Order, OrderCounts } from '../../lib/types';

import { S, STATUS_TONE } from './mobileStyles';

// ── TYPES ────────────────────────────────────────────────────────────


interface MobileDashboardProps {
  filteredOrders: Order[];
  counts: OrderCounts;
  currency: string;
  search: string;
  setSearch: (v: string) => void;
  filterStatus: string;
  setFilterStatus: (v: string) => void;
  setShowFilters: (v: boolean) => void;
  setSelectedOrder: (o: Order) => void;
  setShowAddOrder: (v: boolean) => void;
  isCollapsed: boolean;
  dateRangeActive: boolean;
}

interface CardConfig {
  key: 'pending' | 'ordered' | 'delivered' | 'closed';
  label: string;
  toneClass: string;
  iconBg: string;
  activeBorder: string;
}

// ── CONSTANTS ────────────────────────────────────────────────────────

// Same icon set as desktop's StatCards (src/components/shell/StatCards.tsx).
// Matched semantics: red for Pending, blue for Ordered, teal for Delivered,
// neutral grid for Closed.
//
// CSS var() doesn't reliably resolve inside SVG XML attributes (older iOS
// Safari paints them black). We set `color: var(--*)` on the parent <svg>
// `style` and use `stroke="currentColor"` / `fill="currentColor"` on the
// shapes — works across every browser including older WebKit.
const STAT_ICONS = {
  pending: (
    <svg viewBox="0 0 16 16" width="14" height="14" style={{ color: 'var(--red)' }}>
      <circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <path d="M8 5v3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="8" cy="11" r="0.8" fill="currentColor" />
    </svg>
  ),
  ordered: (
    <svg viewBox="0 0 16 16" width="14" height="14" style={{ color: 'var(--blue)' }}>
      <rect x="3" y="2" width="10" height="12" rx="2" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <path d="M6 6h4M6 9h2.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  ),
  delivered: (
    <svg viewBox="0 0 16 16" width="14" height="14" style={{ color: 'var(--teal)' }}>
      <path d="M4 8.5l2.5 2.5L12 5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  closed: (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none" strokeWidth="1.3" style={{ color: 'var(--text2)', stroke: 'currentColor' }}>
      <rect x="2" y="3"  width="5" height="5" rx="1.5" />
      <rect x="9" y="3"  width="5" height="5" rx="1.5" />
      <rect x="2" y="10" width="5" height="5" rx="1.5" />
      <rect x="9" y="10" width="5" height="5" rx="1.5" />
    </svg>
  ),
};

const CARDS: CardConfig[] = [
  { key: 'pending',   label: 'Pending',  toneClass: 'sc cr m-sc', iconBg: 'var(--red-bg)',   activeBorder: 'var(--red)'   },
  { key: 'ordered',   label: 'On order', toneClass: 'sc cb m-sc', iconBg: 'var(--blue-bg)',  activeBorder: 'var(--blue)'  },
  { key: 'delivered', label: 'Delivered',toneClass: 'sc cg m-sc', iconBg: 'var(--teal-bg)',  activeBorder: 'var(--teal)'  },
  { key: 'closed',    label: 'Closed',   toneClass: 'sc cn m-sc', iconBg: 'var(--surface2)', activeBorder: 'var(--text2)' },
];

// ── MAIN COMPONENT ───────────────────────────────────────────────────

export default function MobileDashboard({
  filteredOrders, counts, currency, search, setSearch,
  filterStatus, setFilterStatus, setShowFilters, setSelectedOrder,
  setShowAddOrder, isCollapsed, dateRangeActive,
}: MobileDashboardProps) {

  // ═══ ORDER ROW ═══
  const OrderRow = ({ o }: { o: Order }) => {
    const items = o.order_items ?? [];
    const primary = items[0];
    const extra = items.length - 1;
    const tone = STATUS_TONE[o.status || ''] || 'gray';
    return (
      <div onClick={() => setSelectedOrder(o)} className="m-row" style={{ display: 'flex', alignItems: 'center', padding: '10px 12px', borderBottom: '0.5px solid var(--border)', cursor: 'pointer' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 'var(--m-text-body)', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{primary?.item_name || 'Order'}</div>
          <div style={{ fontSize: 'var(--m-text-meta)', color: 'var(--text3)', marginTop: 1 }}>
            {o.vendor || '—'} · {o.order_number || '—'}
            {extra > 0 && <span> · +{extra} items</span>}
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 12 }}>
          <div style={{ fontSize: 'var(--m-text-body)', fontWeight: 600 }}>{o.total_cost ? formatCurrency(o.total_cost, currency) : '—'}</div>
          <span className={`pill-u ${tone}`}>{o.status}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="m-tab-content">
      {/* Stat cards — same icon family + colour treatment as desktop StatCards */}
      {!isCollapsed && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6, marginBottom: 8 }}>
          {CARDS.map(card => {
            const active = filterStatus === card.key;
            return (
              <div
                key={card.key}
                className={card.toneClass}
                onClick={() => setFilterStatus(active ? 'all' : card.key)}
                style={{ border: `2px solid ${active ? card.activeBorder : 'transparent'}` }}
                title={`Show ${card.label.toLowerCase()} only`}
              >
                <div className="flex items-center justify-between" style={{ marginBottom: 4 }}>
                  <div className="sl">{card.label}</div>
                  <div
                    style={{
                      width: 24, height: 24, background: card.iconBg,
                      borderRadius: 6,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >{STAT_ICONS[card.key]}</div>
                </div>
                <div className="sn">{counts[card.key]}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Search + Filters + Add */}
      {!isCollapsed && (
        <div style={{ ...S.card, padding: '8px 10px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <svg viewBox="0 0 24 24" style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, fill: 'none', stroke: 'var(--text3)', strokeWidth: 1.5 }}><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search orders..."
              className="input"
              style={{ padding: '8px 10px 8px 28px' }}
            />
          </div>
          <button
            onClick={() => setShowFilters(true)}
            className="btn btn-sm"
            style={{ flexShrink: 0, position: 'relative' }}
          >
            Filters
            {dateRangeActive && (
              <span style={{
                position: 'absolute', top: -3, right: -3,
                width: 8, height: 8, borderRadius: '50%',
                background: 'var(--red)', border: '1.5px solid var(--surface)',
              }} />
            )}
          </button>
          <button
            onClick={() => setShowAddOrder(true)}
            aria-label="Add order"
            className="btn btn-primary btn-icon"
            style={{ flexShrink: 0 }}
          >
            <svg viewBox="0 0 16 16" style={{ width: 14, height: 14, fill: 'none', stroke: 'currentColor', strokeWidth: 2 }}><path d="M8 3v10M3 8h10" /></svg>
          </button>
        </div>
      )}

      {/* Active filter indicator */}
      {filterStatus !== 'all' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, paddingLeft: 4 }}>
          <span style={{ fontSize: 'var(--m-text-label)', color: 'var(--text3)' }}>Showing: {filterStatus.charAt(0).toUpperCase() + filterStatus.slice(1)}</span>
          <button onClick={() => setFilterStatus('all')} style={{ fontSize: 'var(--m-text-label)', color: 'var(--accent)', background: 'none', border: 'none', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>Clear</button>
        </div>
      )}

      {/* Order list */}
      <div style={S.card}>
        {filteredOrders.length === 0 ? (
          <EmptyState
            title="No orders found"
            description={filterStatus !== 'all' || search ? 'Try adjusting your filters' : 'Tap + to add your first order'}
            action={filterStatus === 'all' && !search ? { label: 'Add order', onClick: () => setShowAddOrder(true) } : undefined}
          />
        ) : filteredOrders.map(o => <OrderRow key={o.id} o={o} />)}
      </div>
    </div>
  );
}
